/**
 * Field-level conditional logic ("motor de requisitos") for the public form.
 *
 * A field may declare two optional rule sets keyed off OTHER fields' answers:
 *   - `visible_when`  → the field is rendered only when the rules pass
 *   - `required_when` → the field is required only when the rules pass
 *
 * Rule set shape:
 *   { op: 'and' | 'or', rules: [ { field, cmp, value } ] }
 *     op   defaults to 'and'
 *     cmp  one of: 'eq' | 'neq' | 'in' | 'truthy' | 'falsy'
 *     field is the dependency field's storage name (id)
 *
 * Values are compared as strings (with boolean/checkbox coercion) so a select
 * value 'moral' or a checkbox `true`/'true' all behave intuitively.
 *
 * Pure functions — state in, value out — so they unit-test in isolation.
 */

function coerce(v) {
  if (v === true) return 'true';
  if (v === false) return 'false';
  if (v == null) return '';
  return String(v);
}

function isTruthy(v) {
  const s = coerce(v).toLowerCase();
  return s !== '' && s !== 'false' && s !== '0' && s !== 'no';
}

function evaluateRule(rule, values) {
  if (!rule || !rule.field) return true;
  const actual = values[rule.field];
  switch (rule.cmp) {
    case 'neq':
      return coerce(actual) !== coerce(rule.value);
    case 'in':
      return (Array.isArray(rule.value) ? rule.value : [rule.value])
        .map(coerce)
        .includes(coerce(actual));
    case 'truthy':
      return isTruthy(actual);
    case 'falsy':
      return !isTruthy(actual);
    case 'eq':
    default:
      return coerce(actual) === coerce(rule.value);
  }
}

// Returns true/false when the rule set is present, or null when there is no rule
// set to evaluate (so callers can fall back to defaults).
export function evaluateRuleSet(ruleSet, values = {}) {
  const rules = ruleSet?.rules;
  if (!Array.isArray(rules) || rules.length === 0) return null;
  const op = ruleSet.op === 'or' ? 'or' : 'and';
  return op === 'or'
    ? rules.some(r => evaluateRule(r, values))
    : rules.every(r => evaluateRule(r, values));
}

// A field is visible unless a static `visible: false` hides it (URL-prefill
// fields) or its `visible_when` rule set evaluates to false.
export function isFieldVisible(field, values = {}) {
  if (field?.visible === false) return false;
  const result = evaluateRuleSet(field?.visible_when, values);
  return result === null ? true : result;
}

// Requiredness: `required_when` (when present) overrides the static `required`
// flag; otherwise fall back to `required`.
export function isFieldRequired(field, values = {}) {
  const result = evaluateRuleSet(field?.required_when, values);
  return result === null ? !!field?.required : result;
}
